import {
  CreditCardOutlined,
  GavelOutlined,
  MoreHorizOutlined,
  PrivacyTipOutlined,
  ReceiptLongOutlined,
  SearchOutlined,
  WaterOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { amber, blue, green, grey, purple, red } from "@mui/material/colors";
import { memo, useState } from "react";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";

/* ─────────────────────────────────────────────
   Tone palette
   ─────────────────────────────────────────────
   Status tones drive: side-stripe color, status pill, progress bar, alert
   strip. Kept in one place so a future tone (e.g. "skipped") only needs to
   slot in here.
*/
const TONE = {
  complete: { stripe: green[700], pillFg: green[800], pillBg: green[50] },
  progress: { stripe: blue[600], pillFg: blue[800], pillBg: blue[50] },
  pending: { stripe: amber[700], pillFg: amber[900], pillBg: amber[50] },
  issue: { stripe: red[700], pillFg: red[800], pillBg: red[50] },
  not_ordered: { stripe: grey[300], pillFg: grey[700], pillBg: grey[100] },
};

const PILL_LABEL = {
  complete: "Complete",
  progress: "In progress",
  pending: "Pending",
  issue: "Issue",
  not_ordered: "Not ordered",
};

const VIZ_VALUE_TONE = {
  good: green[700],
  warn: amber[800],
  bad: red[700],
  value: blue[800],
  neutral: grey[800],
};

/* ─── Order-type icon map ─── */
const ORDER_TYPE_ICON = {
  flood: { Icon: WaterOutlined, fg: blue[700], bg: blue[50] },
  funding_shield: { Icon: PrivacyTipOutlined, fg: purple[700], bg: purple[50] },
  appraisal: { Icon: GavelOutlined, fg: amber[800], bg: amber[50] },
  credit: { Icon: CreditCardOutlined, fg: amber[900], bg: amber[50] },
  title: { Icon: ReceiptLongOutlined, fg: green[700], bg: green[50] },
  background: { Icon: SearchOutlined, fg: grey[700], bg: grey[100] },
};

const formatDateShort = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatDate(d, "", "MMM d");
};

const formatDollar = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const OrderCard = memo(({ order }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  if (!order) return null;
  const tone = TONE[order.status] || TONE.not_ordered;
  const iconCfg = ORDER_TYPE_ICON[order.type] || ORDER_TYPE_ICON.background;
  const Icon = iconCfg.Icon;

  const overflowItems = (order.actions || []).filter((a) => a?.overflow);
  const inlineActions = (order.actions || []).filter((a) => !a?.overflow);

  // Disabled/locked order types (WIP flood + funding shield, and the
  // not-yet-integrated placeholder vendors) collapse their action buttons into
  // a single, non-actionable "Coming Soon" chip while those flows are finished.
  const allActions = order.actions || [];
  const isComingSoon =
    order.locked ||
    (allActions.length > 0 && allActions.every((a) => a?.disabled));

  return (
    <Box
      sx={{
        display: "grid",
        // lg+: single row with fixed-width right-hand sections so Status / Viz /
        // Cost / Actions line up across every card. Below lg the fixed columns
        // (~740px) no longer fit, so the sections stack under the identity
        // block instead of crushing it into overlapping text.
        gridTemplateColumns: {
          xs: "5px auto minmax(0, 1fr)",
          lg: "5px auto minmax(0, 1fr) 200px 180px 120px 240px",
        },
        gridTemplateAreas: {
          xs: `
            "stripe icon identity"
            "stripe status status"
            "stripe viz viz"
            "stripe cost cost"
            "stripe actions actions"
            "stripe alert alert"
          `,
          lg: `
            "stripe icon identity status viz cost actions"
            "stripe alert alert alert alert alert alert"
          `,
        },
        alignItems: "stretch",
        background: "#fff",
        border: `1px solid ${grey[200]}`,
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        transition: "border-color 0.15s, box-shadow 0.15s",
        "&:hover": {
          borderColor: grey[300],
          boxShadow: "0 4px 12px rgba(16,24,40,0.06)",
        },
      }}
    >
      {/* Status stripe */}
      <Box sx={{ gridArea: "stripe", background: tone.stripe }} />

      {/* Icon */}
      <Flx
        ac
        sx={{
          gridArea: "icon",
          px: 2,
          py: 1.5,
          borderRight: `1px solid ${grey[100]}`,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "9px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: iconCfg.bg,
            color: iconCfg.fg,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
      </Flx>

      {/* Identity */}
      <Flx
        column
        jc
        sx={{
          gridArea: "identity",
          px: 2,
          py: 1.5,
          minWidth: 0,
          borderRight: { xs: "none", lg: `1px solid ${grey[100]}` },
          borderBottom: { xs: `1px solid ${grey[100]}`, lg: "none" },
        }}
      >
        <Flx ac wrap g={1} sx={{ mb: 0.4 }}>
          <Txt
            sx={{
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: grey[900],
            }}
          >
            {order.name}
          </Txt>
          {order.category && (
            <Box
              sx={{
                fontSize: 10.5,
                px: 0.9,
                py: "1px",
                background: grey[100],
                borderRadius: "4px",
                color: grey[700],
                fontWeight: 500,
              }}
            >
              {order.category}
            </Box>
          )}
          {order.wip && (
            <Tooltip
              title={
                order.locked
                  ? "Work in progress — temporarily disabled while this integration is finalized."
                  : "Work in progress — this integration is being finalized."
              }
              arrow
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.4,
                  fontSize: 10,
                  letterSpacing: "0.06em",
                  px: 0.8,
                  py: "1px",
                  background: amber[100],
                  border: `1px solid ${amber[300]}`,
                  borderRadius: "4px",
                  color: amber[900],
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                WIP
              </Box>
            </Tooltip>
          )}
          {order.externalId && (
            <Box
              sx={{
                fontFamily: "ui-monospace, Menlo, monospace",
                fontSize: 10.5,
                px: 0.7,
                py: "1px",
                background: grey[100],
                borderRadius: "3px",
                color: grey[600],
                fontWeight: 500,
              }}
            >
              #{order.externalId}
            </Box>
          )}
        </Flx>
        <Flx ac wrap g={1}>
          {order.vendor && (
            <Txt sx={{ fontSize: 12, color: grey[700], fontWeight: 500 }}>
              {order.vendor}
            </Txt>
          )}
          {order.timeline?.map((t, i) => (
            <Flx ac g={1} key={`${t}-${i}`}>
              <Box sx={{ color: grey[400] }}>·</Box>
              <Txt sx={{ fontSize: 12, color: grey[600] }}>{t}</Txt>
            </Flx>
          ))}
        </Flx>
      </Flx>

      {/* Status */}
      <Flx
        column
        jc
        g={0.6}
        sx={{
          gridArea: "status",
          px: 2,
          py: 1.5,
          minWidth: 0,
          borderRight: { xs: "none", lg: `1px solid ${grey[100]}` },
          borderBottom: { xs: `1px solid ${grey[100]}`, lg: "none" },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.6,
            alignSelf: "flex-start",
            px: 1,
            py: 0.3,
            borderRadius: "100px",
            background: tone.pillBg,
            color: tone.pillFg,
            fontSize: 11,
            fontWeight: 500,
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: tone.pillFg,
            }}
          />
          {PILL_LABEL[order.status] || order.status}
        </Box>
        {order.statusDetail && (
          <Txt sx={{ fontSize: 11.5, color: grey[600] }}>
            {order.statusDetail}
          </Txt>
        )}
      </Flx>

      {/* Visualization */}
      <Flx
        column
        jc
        g={0.5}
        sx={{
          gridArea: "viz",
          px: 2,
          py: 1.5,
          minWidth: 0,
          alignItems: { xs: "flex-start", lg: "flex-end" },
          borderRight: { xs: "none", lg: `1px solid ${grey[100]}` },
          borderBottom: { xs: `1px solid ${grey[100]}`, lg: "none" },
        }}
      >
        {order.viz?.type === "result" && (
          <>
            {order.viz.label && (
              <Txt
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: grey[500],
                }}
              >
                {order.viz.label}
              </Txt>
            )}
            <Txt
              sx={{
                fontFamily: "ui-monospace, Menlo, monospace",
                fontSize: 14,
                fontWeight: 700,
                color:
                  VIZ_VALUE_TONE[order.viz.valueTone] || VIZ_VALUE_TONE.neutral,
                textAlign: "right",
              }}
            >
              {order.viz.value}
            </Txt>
          </>
        )}
        {order.viz?.type === "progress" && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, order.viz.progress || 0))}
              sx={{
                height: 6,
                borderRadius: "100px",
                background: grey[100],
                "& .MuiLinearProgress-bar": {
                  background: tone.stripe,
                  borderRadius: "100px",
                },
                mb: 0.5,
              }}
            />
            <Flx jc g={0.5}>
              {order.viz.progressLabel && (
                <Txt sx={{ fontSize: 10.5, color: grey[600] }}>
                  {order.viz.progressLabel}
                </Txt>
              )}
            </Flx>
          </Box>
        )}
        {(!order.viz || order.viz.type === "none") && (
          <>
            <Txt
              sx={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: grey[500],
              }}
            >
              Status
            </Txt>
            <Txt
              sx={{
                fontFamily: "ui-monospace, Menlo, monospace",
                fontSize: 14,
                fontWeight: 700,
                color: grey[400],
                textAlign: "right",
              }}
            >
              N/A
            </Txt>
          </>
        )}
      </Flx>

      {/* Cost */}
      <Flx
        column
        jc
        g={0.4}
        sx={{
          gridArea: "cost",
          px: 2,
          py: 1.5,
          minWidth: 0,
          alignItems: { xs: "flex-start", lg: "flex-end" },
          borderRight: { xs: "none", lg: `1px solid ${grey[100]}` },
          borderBottom: { xs: `1px solid ${grey[100]}`, lg: "none" },
        }}
      >
        <Txt
          sx={{
            fontFamily: "ui-monospace, Menlo, monospace",
            fontSize: 13,
            fontWeight: 700,
            color: order.cost ? grey[900] : grey[400],
          }}
        >
          {formatDollar(order.cost) || "—"}
        </Txt>
      </Flx>

      {/* Actions */}
      <Flx ac wrap g={0.5} sx={{ gridArea: "actions", px: 1.5, py: 1.5 }}>
        {isComingSoon ? (
          <Button
            size="small"
            variant="outlined"
            disabled
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            Coming Soon
          </Button>
        ) : (
          <>
            {inlineActions.map((a, i) => (
          <Button
            key={`${a.label}-${i}`}
            size="small"
            variant={
              a.kind === "urgent" || a.kind === "primary"
                ? "contained"
                : "outlined"
            }
            color={a.kind === "urgent" ? "error" : "primary"}
            onClick={a.onClick}
            disabled={a.disabled}
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              fontSize: 11.5,
              fontWeight: 600,
              ...(a.kind === "primary" && {
                background: grey[900],
                "&:hover": { background: grey[800] },
              }),
            }}
          >
            {a.label}
          </Button>
        ))}
        {overflowItems.length > 0 && (
          <>
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ color: grey[600] }}
            >
              <MoreHorizOutlined sx={{ fontSize: 18 }} />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              {overflowItems.map((a, i) => (
                <MenuItem
                  key={`${a.label}-${i}`}
                  onClick={() => {
                    setMenuAnchor(null);
                    a.onClick?.();
                  }}
                  disabled={a.disabled}
                  sx={{ fontSize: 12.5 }}
                >
                  {a.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
          </>
        )}
      </Flx>

      {/* Alert strip (full width, conditional) */}
      {order.alert && (
        <Box
          sx={{
            gridArea: "alert",
            px: 2.5,
            py: 1.25,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            // m: 0.6,
            // borderRadius: 1.2,
            borderBottomLeftRadius: "10px",
            borderBottomRightRadius: "10px",
            // border: "2px solid #ffffff",
            background: order.alert.tone === "issue" ? red[50] : amber[50],
            color: order.alert.tone === "issue" ? red[800] : amber[900],
            border: `1px solid ${
              order.alert.tone === "issue" ? red[100] : amber[100]
            }`,
            // borderTop: `1px solid ${
            //   order.alert.tone === "issue" ? red[100] : amber[100]
            // }`,
          }}
        >
          <WarningAmberOutlined
            sx={{ fontSize: 16, mt: "1px", flexShrink: 0 }}
          />
          <Box>
            {order.alert.title && (
              <Txt
                component="span"
                sx={{ fontSize: 12, fontWeight: 700, mr: 0.5 }}
              >
                {order.alert.title}
              </Txt>
            )}
            {order.alert.message && (
              <Txt component="span" sx={{ fontSize: 12, lineHeight: 1.5 }}>
                {order.alert.message}
              </Txt>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
});

OrderCard.displayName = "OrderCard";

export default OrderCard;

export { TONE, PILL_LABEL, formatDateShort, formatDollar };
